import React from 'react'
import type { RubroRow } from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import { rubroHasValidSelectedSkill } from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import { buildDefaultMetricSheetState } from '../../../../../utils/conversationalMode/metricSheetStateUtils.ts'
import {
  resolveMetricComputationView,
  resolveMetricLegajoStorageUrl
} from '../../../../../utils/conversationalMode/projectAutofillWorkflow.ts'
import {
  classifyMetricLegajoDisplay,
  METRIC_IMPORT_GRID_BUTTON_LABEL,
  metricImportGridTagline,
  metricLegajoReadonlyNotice,
  metricLegajoSourceSectionTitle,
  metricManualGridTagline
} from '../../../../../utils/conversationalMode/metricLegajoDisplayUtils.ts'
import MetricLegajoPreview from './MetricLegajoPreview/index.ts'
import { showQuotationOrphanAfterMetricRemoval } from '../../../../../utils/conversationalMode/rubroMetricBudgetCoherenceUtils.ts'
import { buildSlotOriginActionMeta } from './rubroDeliverableOriginUtils.ts'
import RubroDeliverableSlotActions from './RubroDeliverableSlotActions.tsx'
import type { RubroSlotOriginHandlers } from './RubroSlotOriginHandlers.ts'
import type { RubroDeliverableSourceLineProps } from './rubroDeliverableSourceLineProps.ts'
import styles from './BudgetSheetCompactEntry.module.scss'
import ProjectAutofillInfoNote from './ProjectAutofillInfoNote.tsx'

type SourceLineProps = RubroDeliverableSourceLineProps

type Props = {
  tempId: string
  rubro: RubroRow
  draftLocked: boolean
  isSaving: boolean
  draftAnalyzed: boolean
  updateRubro: (tempId: string, updates: Partial<Omit<RubroRow, 'tempId'>>) => void
  onOpenFullscreen: (tempId: string) => void
  sourceLineProps: SourceLineProps
  originHandlers: RubroSlotOriginHandlers
  originPickerOpen: boolean
  onOriginPickerOpenChange: (open: boolean) => void
  removedSnapshot: Partial<Omit<RubroRow, 'tempId'>> | null
  onRemovedSnapshotChange: (snapshot: Partial<Omit<RubroRow, 'tempId'>> | null) => void
  onImportMetricEdify?: (tempId: string) => void
  importLoading?: boolean
  importError?: string | null
}

const summarizePages = (pages: number[] | undefined): string => {
  if (!pages?.length) return ''
  if (pages.length <= 12) return pages.join(', ')
  return `${pages.slice(0, 12).join(', ')}… (+${pages.length - 12})`
}

const MetricSheetCompactEntry: React.FC<Props> = ({
  tempId,
  rubro,
  draftLocked,
  isSaving,
  draftAnalyzed,
  updateRubro,
  onOpenFullscreen,
  sourceLineProps,
  originHandlers,
  originPickerOpen,
  onOriginPickerOpenChange,
  removedSnapshot,
  onRemovedSnapshotChange,
  onImportMetricEdify,
  importLoading,
  importError
}) => {
  if (draftLocked) return null

  const view = resolveMetricComputationView(rubro)
  const canSkill = rubroHasValidSelectedSkill(rubro)
  const saving = isSaving
  const ms = rubro.metricSheetState
  const legajo = rubro.metricLegajo
  const legajoUrl = resolveMetricLegajoStorageUrl(legajo, sourceLineProps.draftFiles)
  const showRemove = view !== 'missing' || !!ms || !!removedSnapshot
  const rubroRef = {
    tempId,
    detectedLabel: rubro.detectedLabel,
    selectedSkillId: rubro.selectedSkillId
  }

  const ensureGrid = () => {
    if (ms) return
    updateRubro(tempId, { metricSheetState: buildDefaultMetricSheetState(rubroRef) })
  }

  const handleOpenFullscreen = () => {
    if (!canSkill || saving) return
    ensureGrid()
    onOpenFullscreen(tempId)
  }

  const canOpenEditor = canSkill && view === 'editable_grid'
  const primaryLabel = ms ? 'Ver y editar' : 'Cargar cómputo manualmente'

  const originMeta =
    view === 'legajo' && !legajo
      ? buildSlotOriginActionMeta({ slot: 'metricComputation', ...sourceLineProps })
      : null
  const sheetMeta = (() => {
    const rowsLabel =
      ms && ms.rows.length === 0
        ? 'Sin filas todavía'
        : ms
          ? `${ms.rows.length} fila${ms.rows.length === 1 ? '' : 's'}`
          : null
    if (originMeta?.meta && rowsLabel) return `${originMeta.meta} · ${rowsLabel}`
    if (originMeta?.meta) return originMeta.meta
    return rowsLabel
  })()

  const legajoDisplayKind = legajo ? classifyMetricLegajoDisplay(legajo) : null
  const pdfHrefForActions =
    view === 'legajo' && legajo && legajoDisplayKind === 'pdf' && legajoUrl
      ? legajoUrl
      : view === 'legajo' && !legajo
        ? originMeta?.pdfHref ?? null
        : null

  const slotActions =
    showRemove || originPickerOpen || canOpenEditor || !!removedSnapshot ? (
      <RubroDeliverableSlotActions
        slot="metricComputation"
        showRemove={showRemove}
        showPrimary={canOpenEditor}
        primaryLabel={primaryLabel}
        primaryDisabled={saving}
        onPrimary={handleOpenFullscreen}
        meta={sheetMeta}
        pdfHref={pdfHrefForActions}
        originPickerOpen={originPickerOpen}
        onOriginPickerOpenChange={onOriginPickerOpenChange}
        removedSnapshot={removedSnapshot}
        onRemovedSnapshotChange={onRemovedSnapshotChange}
        {...originHandlers}
      />
    ) : null

  const importBlock =
    view === 'legajo' && rubro.canImportMetricEdifySheet && draftAnalyzed && onImportMetricEdify ? (
      <div className={styles.sheetPanel}>
        <p className={styles.sheetMainTitle}>Grilla de cómputo</p>
        <p className={styles.sheetTagline}>{metricImportGridTagline()}</p>
        {importError ? (
          <ProjectAutofillInfoNote tone="caution" className={styles.hint}>
            {importError}
          </ProjectAutofillInfoNote>
        ) : null}
        <div className={styles.actionsRow}>
          <button
            type="button"
            className={styles.primaryInline}
            disabled={saving || importLoading}
            onClick={() => onImportMetricEdify(tempId)}
          >
            {importLoading ? 'Armando grilla…' : METRIC_IMPORT_GRID_BUTTON_LABEL}
          </button>
        </div>
      </div>
    ) : null

  const manualGridBlock =
    view === 'legajo' && canSkill && !ms ? (
      <div className={styles.sheetPanel}>
        <p className={styles.sheetMainTitle}>Grilla de cómputo</p>
        <p className={styles.sheetTagline}>{metricManualGridTagline()}</p>
        <div className={styles.actionsRow}>
          <button
            type="button"
            className={styles.primaryInline}
            disabled={saving}
            onClick={handleOpenFullscreen}
          >
            Cargar grilla manualmente
          </button>
        </div>
      </div>
    ) : null

  const legajoBlock =
    view === 'legajo' && legajo ? (
      <div className={styles.sheetPanel}>
        <p className={styles.sheetMainTitle}>{metricLegajoSourceSectionTitle(legajoDisplayKind)}</p>
        <ProjectAutofillInfoNote variant="rubroInfo" className={styles.hint}>
          {metricLegajoReadonlyNotice(legajoDisplayKind)}
        </ProjectAutofillInfoNote>
        <MetricLegajoPreview
          legajo={legajo}
          storageUrl={legajoUrl}
          summarizePages={summarizePages}
        />
      </div>
    ) : view === 'legajo' && !legajo ? (
      <div className={styles.sheetPanel}>
        <p className={styles.sheetMainTitle}>Archivo original</p>
        <ProjectAutofillInfoNote variant="rubroInfo" className={styles.hint}>
          Detectamos cómputo en tu documentación. Acá solo podés revisar el origen; para editarlo en Edify, armá la
          grilla con las opciones de abajo.
        </ProjectAutofillInfoNote>
        <p className={styles.sheetOfferText}>
          <strong>Detectamos</strong> cómputo en la documentación de este rubro. Revisá el origen o armá la grilla
          para editarlo acá.
        </p>
      </div>
    ) : null

  if (originPickerOpen) {
    return <div className={styles.blockEmbedded}>{slotActions}</div>
  }

  const quotationOrphan = showQuotationOrphanAfterMetricRemoval(rubro, removedSnapshot)
  const metricWasRemoved = !!removedSnapshot && view === 'missing' && !ms

  if (metricWasRemoved) {
    return (
      <div className={styles.blockEmbedded}>
        {quotationOrphan ? (
          <ProjectAutofillInfoNote tone="caution" className={styles.hint}>
            Sin cómputo métrico no se adjuntará la planilla de cotización que habíamos generado para este rubro.
          </ProjectAutofillInfoNote>
        ) : null}
        <p className={styles.sheetOfferText}>
          <strong>Quitaste</strong> el cómputo de este rubro. Restaurá el detectado o cargá el tuyo.
        </p>
        {slotActions}
      </div>
    )
  }

  if (view === 'missing') {
    return (
      <div className={styles.blockEmbedded}>
        <p className={styles.sheetOfferText}>
          <strong>No detectamos</strong> cómputo métrico en la documentación de este rubro.
        </p>
        {!canSkill ? (
          <ProjectAutofillInfoNote className={styles.hint}>
            Elegí el rubro de la lista para continuar.
          </ProjectAutofillInfoNote>
        ) : null}
        {slotActions}
      </div>
    )
  }

  if (view === 'legajo') {
    return (
      <div className={styles.blockEmbedded}>
        {legajoBlock}
        {importBlock}
        {manualGridBlock}
        {!canSkill ? (
          <ProjectAutofillInfoNote className={styles.hint}>Elegí el rubro de la lista para continuar.</ProjectAutofillInfoNote>
        ) : null}
        {slotActions}
      </div>
    )
  }

  const gridLeadStrong =
    ms?.source === 'imported_from_legajo'
      ? 'Armamos'
      : ms?.source === 'user_edited'
        ? 'Editaste'
        : 'Generamos'
  const gridLeadRest =
    ms?.source === 'imported_from_legajo'
      ? ' la grilla de cómputo desde tu archivo. Revisala antes de publicar.'
      : ms?.source === 'user_edited'
        ? ' el cómputo métrico de este rubro.'
        : ' el cómputo métrico para este rubro. Revisalo antes de publicar.'

  return (
    <div className={styles.blockEmbedded}>
      <p className={styles.sheetOfferText}>
        <strong>{gridLeadStrong}</strong>
        {gridLeadRest}
      </p>
      {!canSkill ? (
        <ProjectAutofillInfoNote className={styles.hint}>Elegí el rubro de la lista para editar.</ProjectAutofillInfoNote>
      ) : null}
      {slotActions}
    </div>
  )
}

export default MetricSheetCompactEntry
